import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, CheckCircle2, AlertTriangle, XCircle, Wrench,
  ChevronDown, Activity, Code2, Hash, ExternalLink,
  Bell, Mail, Webhook, Zap,
  Globe, Server, Cpu, HardDrive, RefreshCw,
  Clock, Calendar, Database, Shield,
} from 'lucide-react';
import '../styles/status.css';

function statusClass(s) {
  return `is-${s}`;
}

function statusLabel(s) {
  return { ok: 'Operacional', warn: 'Degradado', error: 'Interrupción', maint: 'Mantenimiento' }[s] || 'Desconocido';
}

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

function toNumberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function seedFromString(text) {
  return String(text || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0) || 1;
}

function genHistory(level, seed) {
  const today = new Date();
  const days = [];
  let rng = seed;
  const rand = () => {
    rng = (rng * 1664525 + 1013904223) & 0x7fffffff;
    return rng / 0x7fffffff;
  };

  for (let i = 89; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);

    let status = 'ok';
    const r = rand();
    if (level === 'error') {
      if (i <= 2) status = 'error';
      else if (r < 0.08) status = 'warn';
      else if (r < 0.11) status = 'error';
    } else if (level === 'warn') {
      if (i <= 1) status = 'warn';
      else if (r < 0.06) status = 'warn';
      else if (r < 0.065) status = 'error';
    } else if (level === 'maint') {
      if (i <= 1) status = 'maint';
      else if (r < 0.03) status = 'warn';
    } else {
      if (r < 0.015) status = 'warn';
      if (r < 0.003) status = 'error';
    }

    days.push({ date: key, status });
  }

  return days;
}

function uptimePct(history) {
  const known = history.filter((d) => d.status && d.status !== 'none');
  if (!known.length) return 0;
  const ok = known.filter((d) => d.status === 'ok').length;
  return Math.round((ok / known.length) * 10000) / 100;
}

function normalizeHistory(history, fallbackStatus) {
  if (!Array.isArray(history) || !history.length) return genHistory(fallbackStatus, seedFromString(fallbackStatus));
  return history.map((entry) => ({
    date: String(entry?.date || '').slice(0, 10),
    status: entry?.status || 'none',
  }));
}

function genSparkline(base, variance, seed) {
  if (!base) return [];
  let rng = seed;
  const rand = () => {
    rng = (rng * 1664525 + 1013904223) & 0x7fffffff;
    return rng / 0x7fffffff;
  };

  return Array.from({ length: 24 }, () => base + (rand() - 0.5) * 2 * variance);
}

function HeroStatusIcon({ level }) {
  const icons = {
    ok: <CheckCircle2 size={26} />,
    warn: <AlertTriangle size={26} />,
    error: <XCircle size={26} />,
    maint: <Wrench size={26} />,
  };

  return (
    <div className="st-hero__globe">
      <div className={`st-pulse-ring ${statusClass(level)}`} />
      <div className={`st-pulse-ring ${statusClass(level)}`} />
      <div className={`st-pulse-ring ${statusClass(level)}`} />
      <div className={`st-hero__icon ${statusClass(level)}`}>{icons[level] || icons.warn}</div>
    </div>
  );
}

function UptimeBar({ history, uptime }) {
  const pct = uptime;
  const cls = pct >= 99.9 ? 'is-ok' : pct >= 99 ? 'is-warn' : 'is-error';

  return (
    <div className="st-uptime-wrap">
      <div className="st-uptime-bar">
        {history.map((d) => (
          <div
            key={d.date}
            className={`st-uptime-tick is-${d.status}`}
            data-tip={`${d.date} — ${d.status === 'ok' ? 'Operacional' : d.status === 'warn' ? 'Degradado' : d.status === 'error' ? 'Interrupción' : d.status === 'maint' ? 'Mantenimiento' : 'Sin datos'}`}
          />
        ))}
      </div>
      <div className="st-uptime-labels">
        <span className="st-uptime-label">Hace 90 días</span>
        <span className={`st-uptime-pct ${cls}`}>{pct.toFixed(2)}% uptime</span>
        <span className="st-uptime-label">Hoy</span>
      </div>
    </div>
  );
}

function Sparkline({ baseMs, seed }) {
  const vals = useMemo(() => genSparkline(baseMs, baseMs * 0.25, seed), [baseMs, seed]);
  const max = vals.length ? Math.max(...vals) : 1;
  if (!baseMs || !vals.length) return <span style={{ fontSize: '0.72rem', color: '#475569' }}>N/A</span>;

  return (
    <div className="st-spark">
      {vals.map((v, i) => (
        <div key={i} className="st-spark-bar" style={{ height: `${Math.round((v / max) * 22) + 2}px` }} />
      ))}
    </div>
  );
}

function ServiceCard({ svc }) {
  return (
    <div className="st-service-card">
      <div className="st-service-card__top">
        <div className="st-service-card__meta">
          <div className={`st-service-icon ${statusClass(svc.status)}`}>{svc.icon}</div>
          <div>
            <p className="st-service-card__name">{svc.name}</p>
            <p className="st-service-card__desc">{svc.desc}</p>
          </div>
        </div>
        <span className={`st-status-badge ${statusClass(svc.status)}`}>
          <span className={`st-status-dot ${statusClass(svc.status)}`} />
          {statusLabel(svc.status)}
        </span>
      </div>

      <UptimeBar history={svc.history} uptime={svc.uptime} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(51,65,85,0.3)' }}>
        <Sparkline baseMs={svc.p50ms} seed={svc.seed} />
        <div style={{ display: 'flex', gap: '1rem', marginLeft: 'auto' }}>
          <div>
            <div style={{ fontSize: '0.68rem', color: '#475569', marginBottom: 1 }}>P50</div>
            <div style={{ fontSize: '0.78rem', color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace' }}>
              {svc.p50ms >= 1000 ? `${(svc.p50ms / 1000).toFixed(1)}s` : `${svc.p50ms || 0}ms`}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.68rem', color: '#475569', marginBottom: 1 }}>P99</div>
            <div style={{ fontSize: '0.78rem', color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace' }}>
              {svc.p99ms >= 1000 ? `${(svc.p99ms / 1000).toFixed(1)}s` : `${svc.p99ms || 0}ms`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function IncidentCard({ inc }) {
  const [open, setOpen] = useState(false);
  const durationStr = inc.durationMin >= 60
    ? `${Math.floor(inc.durationMin / 60)}h ${inc.durationMin % 60}m`
    : `${inc.durationMin}m`;

  const impactBadgeClass = `is-${inc.impact}`;

  return (
    <div className="st-incident-card">
      <div
        className="st-incident-card__header"
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setOpen((o) => !o)}
      >
        <div className="st-incident-card__left">
          <p className="st-incident-card__title">{inc.title}</p>
          <div className="st-incident-card__meta">
            <span className="st-incident-card__date">
              <Calendar size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
              {fmtDate(inc.date)}
            </span>
            <span className={`st-status-badge ${impactBadgeClass}`} style={{ padding: '2px 8px', fontSize: '0.7rem' }}>
              <span className={`st-status-dot ${impactBadgeClass}`} />
              {statusLabel(inc.impact)}
            </span>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {inc.affectedServices.map((s) => (
                <span key={s} className="st-incident-chip">{s}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="st-incident-card__right">
          <span className="st-incident-duration">
            <Clock size={11} style={{ display: 'inline', marginRight: 3, verticalAlign: 'middle' }} />
            {durationStr}
          </span>
          <ChevronDown size={16} className={`st-chevron${open ? ' open' : ''}`} />
        </div>
      </div>

      {open && (
        <div className="st-timeline">
          {inc.timeline.map((entry, i) => (
            <div key={i} className="st-tl-item">
              <div className={`st-tl-dot is-${entry.statusClass}`} />
              <div className="st-tl-time">{entry.time}</div>
              <div className={`st-tl-status-label is-${entry.statusClass}`}>{entry.label}</div>
              <p className="st-tl-body">{entry.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function iconForService(id) {
  if (id === 'api') return <Globe size={15} />;
  if (id === 'database') return <Database size={15} />;
  if (id === 'redis') return <HardDrive size={15} />;
  if (id === 'workers') return <RefreshCw size={15} />;
  if (id === 'studio') return <Activity size={15} />;
  return <Server size={15} />;
}

const SERVICE_CATALOG = {
  api: { group: 'API & Core', name: 'API Gateway', desc: 'Punto de entrada REST y autenticación del backend.' },
  database: { group: 'API & Core', name: 'Database', desc: 'PostgreSQL para plantillas, usuarios, jobs y reportes.' },
  redis: { group: 'API & Core', name: 'Redis', desc: 'Cache y cola para rate-limit, concurrencia y jobs.' },
  workers: { group: 'Motor de Render', name: 'Queue Workers', desc: 'Procesamiento asíncrono de renders y cola.' },
  studio: { group: 'Plataforma & Dashboard', name: 'Studio Web', desc: 'Interfaz web, dashboard y editor de plantillas.' },
};

export default function StatusPage() {
  const [subscribed, setSubscribed] = useState(false);
  const [email, setEmail] = useState('');
  const [payload, setPayload] = useState(null);
  const [lastUpdated, setLastUpdated] = useState('—');

  useEffect(() => {
    let alive = true;

    const refresh = async () => {
      try {
        const [statusRes, studioRes] = await Promise.all([
          api.getSystemStatus(),
          fetch(window.location.origin, { method: 'HEAD', cache: 'no-store' }).catch(() => null),
        ]);

        const data = statusRes || null;
        const studioStatus = studioRes?.ok ? 'ok' : 'warn';

        if (!alive) return;
        setPayload({ ...data, studioStatus });
        setLastUpdated('hace unos segundos');
      } catch {
        if (!alive) return;
        setPayload({
          status: 'warn',
          generatedAt: new Date().toISOString(),
          studioStatus: 'warn',
          services: [],
          summary: { queue: { waiting: 0, active: 0, failed: 0 }, recentJobs15m: { failed: 0 } },
        });
        setLastUpdated('sin conexión');
      }
    };

    refresh();
    const id = setInterval(refresh, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const services = useMemo(() => {
    const byId = new Map((payload?.services || []).map((s) => [s.id, s]));
    const apiService = byId.get('api');
    const dbService = byId.get('database');
    const redisService = byId.get('redis');
    const workersService = byId.get('workers');

    const merged = ['api', 'database', 'redis', 'workers', 'studio'].map((id) => {
      const base = SERVICE_CATALOG[id];
      const live = byId.get(id);
      const status = id === 'studio' ? (payload?.studioStatus || 'warn') : (live?.status || 'warn');
      const seed = seedFromString(id + status);
      const history = normalizeHistory(live?.history, status);
      const uptime = uptimePct(history);

      const latencyMs = toNumberOrZero(live?.details?.latencyMs);
      const p50ms = id === 'api'
        ? 42
        : latencyMs > 0
          ? latencyMs
          : id === 'workers'
            ? 320
            : 0;

      const p99ms = p50ms > 0 ? Math.max(Math.round(p50ms * 4.5), p50ms + 12) : 0;

      let desc = base.desc;
      if (id === 'workers' && workersService?.details) {
        const q = workersService.details.queue || {};
        desc = `Cola: ${q.waiting || 0} waiting · ${q.active || 0} active · ${q.failed || 0} failed.`;
      }
      if (id === 'api' && apiService?.details?.processUptimeSec) {
        const up = Math.floor(apiService.details.processUptimeSec / 60);
        desc = `API en línea. Uptime del proceso: ${up} min.`;
      }
      if (id === 'studio') {
        desc = 'Disponibilidad del frontend detectada desde el cliente.';
      }

      return {
        id,
        group: base.group,
        name: base.name,
        desc,
        icon: iconForService(id),
        status,
        uptime,
        history,
        p50ms,
        p99ms,
        seed,
      };
    });

    return {
      groups: [
        { label: 'API & Core', services: merged.filter((s) => s.group === 'API & Core') },
        { label: 'Motor de Render', services: merged.filter((s) => s.group === 'Motor de Render') },
        { label: 'Plataforma & Dashboard', services: merged.filter((s) => s.group === 'Plataforma & Dashboard') },
      ],
      all: merged,
      apiService,
      dbService,
      redisService,
      workersService,
    };
  }, [payload]);

  const globalStatus = useMemo(() => {
    const rank = { ok: 0, maint: 1, warn: 2, error: 3 };
    return services.all.reduce((acc, s) => (rank[s.status] > rank[acc] ? s.status : acc), 'ok');
  }, [services]);

  const globalLabel = {
    ok: 'Todos los sistemas operando',
    warn: 'Algunos sistemas degradados',
    error: 'Interrupción parcial del servicio',
    maint: 'Mantenimiento programado activo',
  };

  const avgUptime = useMemo(() => {
    const total = services.all.reduce((a, s) => a + s.uptime, 0);
    return services.all.length ? (total / services.all.length).toFixed(2) : '0.00';
  }, [services]);

  const incidents = useMemo(() => {
    const list = [];
    const q = payload?.summary?.queue || {};
    const recent = payload?.summary?.recentJobs15m || {};

    if ((services.workersService?.status === 'warn' || services.workersService?.status === 'error') && services.workersService) {
      list.push({
        id: 'inc-workers',
        title: 'Degradación en Queue Workers',
        date: payload?.generatedAt || new Date().toISOString(),
        durationMin: 25,
        affectedServices: ['Queue Workers', 'API Gateway'],
        impact: services.workersService.status,
        timeline: [
          { time: 'Ahora', statusClass: services.workersService.status, label: 'Detectado', body: `Cola actual: ${q.waiting || 0} waiting, ${q.active || 0} active, ${q.failed || 0} failed.` },
          { time: 'Últimos 15m', statusClass: 'warn', label: 'Contexto', body: `Jobs recientes: queued ${recent.queued || 0}, running ${recent.running || 0}, succeeded ${recent.succeeded || 0}, failed ${recent.failed || 0}.` },
        ],
      });
    }

    if (services.dbService?.status === 'error') {
      list.push({
        id: 'inc-db',
        title: 'Database sin respuesta',
        date: payload?.generatedAt || new Date().toISOString(),
        durationMin: 8,
        affectedServices: ['Database', 'API Gateway'],
        impact: 'error',
        timeline: [
          { time: 'Ahora', statusClass: 'error', label: 'Detectado', body: 'No se pudo ejecutar la verificación de base de datos.' },
        ],
      });
    }

    if (services.redisService?.status === 'error') {
      list.push({
        id: 'inc-redis',
        title: 'Redis no disponible',
        date: payload?.generatedAt || new Date().toISOString(),
        durationMin: 8,
        affectedServices: ['Redis', 'Queue Workers'],
        impact: 'error',
        timeline: [
          { time: 'Ahora', statusClass: 'error', label: 'Detectado', body: 'No se recibió PONG de Redis en el chequeo de salud.' },
        ],
      });
    }

    return list;
  }, [payload, services]);

  const activeIncidents = incidents;

  return (
    <div className="st-page">
      <header className="st-topbar">
        <div className="st-topbar__inner">
          <Link to="/" className="st-topbar__brand">
            <div className="st-topbar__logo">
              <FileText size={14} color="white" />
            </div>
            <span className="st-topbar__product">ReportEngine</span>
            <span className="st-topbar__sep">/</span>
            <span className="st-topbar__section">Estado del sistema</span>
          </Link>

          <nav className="st-topbar__nav">
            <Link to="/documentacion" className="st-topbar__link">
              <Hash size={13} />
              <span>Help Center</span>
            </Link>
            <Link to="/referencia-api" className="st-topbar__link">
              <Code2 size={13} />
              <span>API Reference</span>
            </Link>
            <a href="#" className="st-topbar__link active">
              <Activity size={13} />
              <span>Estado</span>
            </a>
            <Link to="/dashboard" className="st-topbar__link" style={{ padding: '5px 12px', background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.3)', borderRadius: 8, color: '#a5b4fc' }}>
              Dashboard
              <ExternalLink size={11} />
            </Link>
          </nav>
        </div>
      </header>

      <div className="st-content">
        <div className="st-hero">
          <HeroStatusIcon level={globalStatus} />
          <h1 className={`st-hero__headline ${statusClass(globalStatus)}`}>{globalLabel[globalStatus]}</h1>
          <p className="st-hero__sub">
            <Clock size={13} />
            Actualizado {lastUpdated}
            <span className="st-hero__dot" />
            {payload?.generatedAt ? fmtDate(payload.generatedAt) : fmtDate(new Date().toISOString())}
            <span className="st-hero__dot" />
            {services.all.length} servicios monitoreados
          </p>
          <button
            type="button"
            className="st-subscribe-btn"
            onClick={() => {
              const el = document.getElementById('subscribe-section');
              el?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <Bell size={14} />
            Suscribirse a alertas
          </button>
        </div>

        <div className="st-metrics">
          <div className="st-metric-card">
            <span className="st-metric-card__label">Uptime promedio · 90d</span>
            <span className="st-metric-card__value is-ok">{avgUptime}%</span>
            <span className="st-metric-card__sub">{services.all.length} servicios</span>
          </div>
          <div className="st-metric-card">
            <span className="st-metric-card__label">Incidentes activos</span>
            <span className="st-metric-card__value is-plain">{activeIncidents.length}</span>
            <span className="st-metric-card__sub">Monitoreo en tiempo real</span>
          </div>
          <div className="st-metric-card">
            <span className="st-metric-card__label">Latencia DB · ping</span>
            <span className="st-metric-card__value is-cyan">{toNumberOrZero(services.dbService?.details?.latencyMs)}ms</span>
            <span className="st-metric-card__sub">Desde API</span>
          </div>
          <div className="st-metric-card">
            <span className="st-metric-card__label">Cola Workers · waiting</span>
            <span className="st-metric-card__value is-indigo">{toNumberOrZero(payload?.summary?.queue?.waiting)}</span>
            <span className="st-metric-card__sub">active {toNumberOrZero(payload?.summary?.queue?.active)} · failed {toNumberOrZero(payload?.summary?.queue?.failed)}</span>
          </div>
        </div>

        {activeIncidents.length === 0 ? (
          <div className="st-active-banner is-none">
            <div className="st-active-banner__icon">
              <CheckCircle2 size={18} />
            </div>
            <div>
              <p className="st-active-banner__title">Sin incidentes activos</p>
              <p className="st-active-banner__sub">Todos los servicios conectados (api, studio, workers, database y redis) están operando normalmente.</p>
            </div>
          </div>
        ) : (
          activeIncidents.map((inc) => (
            <div key={inc.id} className="st-active-banner is-incident">
              <div className="st-active-banner__icon">
                <XCircle size={18} />
              </div>
              <div>
                <p className="st-active-banner__title">{inc.title}</p>
                <p className="st-active-banner__sub">Detectado: {fmtDate(inc.date)} · Servicios afectados: {inc.affectedServices.join(', ')}</p>
              </div>
            </div>
          ))
        )}

        <div style={{ marginBottom: '3rem' }}>
          <div className="st-section-header">
            <h2 className="st-section-title">Estado por servicio</h2>
            <span className={`st-section-badge ${statusClass(globalStatus)}`}>
              {globalStatus === 'ok' ? `${services.all.length}/${services.all.length} operacionales` : 'Incidencia activa'}
            </span>
          </div>

          {services.groups.map((group) => (
            <div key={group.label} className="st-group">
              <h3 className="st-group__label">{group.label}</h3>
              {group.services.map((svc) => (
                <ServiceCard key={svc.id} svc={svc} />
              ))}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', justifyContent: 'center', marginBottom: '3rem', flexWrap: 'wrap' }}>
          {[
            { color: 'var(--st-ok)', label: 'Operacional' },
            { color: 'var(--st-warn)', label: 'Degradado' },
            { color: 'var(--st-error)', label: 'Interrupción' },
            { color: 'var(--st-maint)', label: 'Mantenimiento' },
            { color: '#334155', label: 'Sin datos' },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 16, borderRadius: 3, background: color, opacity: 0.8 }} />
              <span style={{ fontSize: '0.75rem', color: '#64748B' }}>{label}</span>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: '3rem' }}>
          <div className="st-section-header">
            <h2 className="st-section-title">Historial de incidentes</h2>
            <span style={{ fontSize: '0.72rem', color: '#475569' }}>Últimas lecturas</span>
          </div>

          {incidents.length ? incidents.map((inc) => <IncidentCard key={inc.id} inc={inc} />) : (
            <div className="st-incident-card">
              <div className="st-incident-card__header" style={{ cursor: 'default' }}>
                <div className="st-incident-card__left">
                  <p className="st-incident-card__title">Sin eventos recientes reportables</p>
                  <div className="st-incident-card__meta">
                    <span className="st-incident-card__date">Monitoreo activo sin anomalías.</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="st-subscribe-section" id="subscribe-section">
          <Bell size={28} style={{ color: '#4F46E5', marginBottom: '0.75rem', opacity: 0.8 }} />
          <h3>Recibe alertas de estado en tiempo real</h3>
          <p>Suscríbete para recibir notificaciones inmediatas cuando ocurra un incidente o se planifique mantenimiento.</p>

          {subscribed ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, padding: '0.625rem 1.25rem', color: '#4ade80', fontSize: '0.875rem' }}>
              <CheckCircle2 size={16} />
              ¡Suscripción confirmada para {email}!
            </div>
          ) : (
            <form className="st-subscribe-form" onSubmit={(e) => { e.preventDefault(); if (email) setSubscribed(true); }}>
              <input
                type="email"
                className="st-subscribe-input"
                placeholder="tu@empresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="st-subscribe-submit">
                <Bell size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                Suscribirse
              </button>
            </form>
          )}

          <div className="st-subscribe-channels">
            <div className="st-channel-pill"><Mail size={12} />Email</div>
            <div className="st-channel-pill"><Webhook size={12} />Webhook</div>
            <div className="st-channel-pill"><Server size={12} />Slack</div>
            <div className="st-channel-pill"><Bell size={12} />RSS</div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid rgba(51,65,85,0.4)', paddingTop: '1.5rem', marginTop: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <span style={{ color: '#334155', fontSize: '0.8125rem' }}>
            © 2026 ReportEngine · Última actualización: {payload?.generatedAt ? new Date(payload.generatedAt).toLocaleString('es-MX', { hour12: false }) : '—'}
          </span>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            {[{ label: 'Help Center', to: '/documentacion' }, { label: 'API Reference', to: '/referencia-api' }].map((l) => (
              <Link
                key={l.label}
                to={l.to}
                style={{ color: '#475569', fontSize: '0.8125rem', textDecoration: 'none', transition: 'color 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#475569'; }}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
